import React, { useCallback, useMemo } from 'react';
import { Box, Toggle, Flex, TextV2 } from '@withjoy/joykit';
import { useSharedRegistryTranslations } from '@apps/registry/SharedRegistry.i18n';
import { useTranslation } from '@shared/core';
import type { FormikProps } from 'formik';
import type { AddCashFundDialogFields, CreditCardCurrencyMismatch } from '../../../../types';
import { useHistory } from '@shared/core';
import { useLocation } from '@react-router';
import { useCreditCardSetupTelemetry } from '@apps/registry/admin/components/CreditCardSetup/CreditCardSetUp.telemetry';
import { styles } from '../../CashFundPaymentMethod.style';
import { ExpressPayLogos } from '@shared/components/ExpressPayLogos';
import { DottedTextButton } from '@shared/components/DottedButton/DottedTextButton';

type CreditCardToggleProps = Readonly<{
  formik: FormikProps<AddCashFundDialogFields>;
  canEnableCreditCard: boolean;
  /**
   * Lets a host set up their payout account without leaving the current flow. When omitted the
   * toggle falls back to the `?setupCreditCard=true` URL hop, which opens the standalone dialog.
   */
  onRequestSetup?: () => void;
  /**
   * NREG-3466: set when cards cannot be collected on this fund for a currency reason payout setup
   * cannot fix, so the toggle explains why instead of prompting for setup.
   */
  currencyMismatch?: CreditCardCurrencyMismatch | null;
  /**
   * True while the payout account is still being fetched. Eligibility fails closed during that
   * window, so the toggle would otherwise read as "not set up" and send an already-set-up host into
   * the Stripe flow. Hold it inert until we know.
   */
  isEligibilityLoading?: boolean;
}>;

export const CreditCardToggle: React.FC<CreditCardToggleProps> = props => {
  const { canEnableCreditCard, onRequestSetup, currencyMismatch, isEligibilityLoading } = props;
  const location = useLocation();
  const history = useHistory();
  const telemetry = useCreditCardSetupTelemetry();
  const { getCashFundDialogTranslations } = useSharedRegistryTranslations();
  const { creditCardOption, recommended, fastCheckoutWith } = getCashFundDialogTranslations();
  const { tq } = useTranslation('sharedRegistry');
  const setUpCreditCardLocation = useMemo(
    () => (location.search ? `${location.pathname}${location.search}&setupCreditCard=true` : `${location.pathname}?setupCreditCard=true`),
    [location],
  );

  const onToggleChange = useCallback(
    e => {
      // Checked first: neither an unresolved payout account nor a currency mismatch is something the
      // setup flow can resolve here, so the toggle stays inert in both cases. The notice below
      // carries the explanation for a mismatch.
      if (isEligibilityLoading || currencyMismatch) {
        return;
      }
      if (canEnableCreditCard) {
        props.formik.setFieldTouched('enableJoyCredit', true, false);
        return props.formik.handleChange(e);
      }
      telemetry.toggleCreditCardOption();
      if (onRequestSetup) {
        return onRequestSetup();
      }
      return history.push(setUpCreditCardLocation);
    },
    [canEnableCreditCard, currencyMismatch, isEligibilityLoading, history, setUpCreditCardLocation, props.formik, telemetry, onRequestSetup],
  );

  const isUnavailable = !!currencyMismatch;
  const isEnabled = props.formik.values.enableJoyCredit;

  const description = (
    <Flex
      flexDirection="column"
      gap={2}
    >
      <Flex alignItems="center">
        <TextV2
          typographyVariant="hed1"
          color={isUnavailable ? 'mono8' : undefined}
          marginRight={3}
          whiteSpace={'nowrap'}
        >
          {creditCardOption}
        </TextV2>
        {!isUnavailable && !isEnabled && (
          <Box
            border="1px solid"
            borderColor="#4751F8"
            borderRadius="4px"
            paddingX={1}
          >
            <TextV2
              typographyVariant="label3"
              color="#4751F8"
            >
              {recommended}
            </TextV2>
          </Box>
        )}
      </Flex>
      <TextV2
        typographyVariant="label1"
        color="mono8"
      >
        {isUnavailable ? tq`Automatic tracking · Apple Pay, Google Pay` : tq`No pesky redirects. Automatic tracking. Transaction fees apply.`}
      </TextV2>
      {!isUnavailable && (
        <Flex alignItems="center">
          <TextV2
            typographyVariant="label1"
            color="mono14"
            marginRight={2}
          >
            {fastCheckoutWith}
          </TextV2>
          <ExpressPayLogos currencyCode={props.formik.values.currencyCode} />
        </Flex>
      )}
    </Flex>
  );

  const toggle = (
    <Flex justifyContent="center">
      <Toggle
        label=""
        hideLabel={true}
        checked={isEnabled}
        name="enableJoyCredit"
        onChange={onToggleChange}
        disabled={isUnavailable || !!isEligibilityLoading}
      />
    </Flex>
  );

  return (
    <Box marginTop={5}>
      <Flex __css={styles.appPlatFormToggleWrapper({ isFeatured: !isUnavailable && !isEnabled, isUnavailable })}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          gap={5}
        >
          {description}
          {toggle}
        </Flex>
        {currencyMismatch && (
          <Box
            borderTop="1px solid"
            borderColor="mono3"
            paddingTop={5}
          >
            <CurrencyMismatchNotice currencyMismatch={currencyMismatch} />
          </Box>
        )}
      </Flex>
    </Box>
  );
};

const ACTION_TAG = /<0>(.*?)<\/0>/;

type SentenceWithActionProps = Readonly<{
  text: string;
  fallbackLabel: string;
  renderAction?: (label: string) => React.ReactNode;
}>;

const SentenceWithAction: React.FC<SentenceWithActionProps> = ({ text, fallbackLabel, renderAction }) => {
  const match = ACTION_TAG.exec(text);
  if (!match) {
    return (
      <>
        {text}
        {renderAction && <> {renderAction(fallbackLabel)}</>}
      </>
    );
  }
  const label = match[1];
  return (
    <>
      {text.slice(0, match.index)}
      {renderAction ? renderAction(label) : label}
      {text.slice(match.index + match[0].length)}
    </>
  );
};

const CurrencyMismatchNotice: React.FC<Readonly<{ currencyMismatch: CreditCardCurrencyMismatch }>> = ({ currencyMismatch }) => {
  const { tq } = useTranslation('sharedRegistry');

  if (currencyMismatch.kind === 'payout') {
    const { registryCurrencyCode, payoutCurrencyCode, onSwitchRegistryCurrency, isSwitchingRegistryCurrency } = currencyMismatch;
    return (
      <TextV2
        typographyVariant="label1"
        color="mono12"
      >
        <SentenceWithAction
          text={tq`Your registry collects in ${registryCurrencyCode}, but your payout account is in ${payoutCurrencyCode}. <0>Switch your registry to ${payoutCurrencyCode}</0> to accept cards. Guests will then see amounts in ${payoutCurrencyCode}.`}
          fallbackLabel={tq`Switch your registry to ${payoutCurrencyCode}`}
          renderAction={label => (
            <DottedTextButton
              typographyVariant="label1"
              onClick={onSwitchRegistryCurrency}
              isDisabled={isSwitchingRegistryCurrency}
            >
              {label}
            </DottedTextButton>
          )}
        />
      </TextV2>
    );
  }

  const { registryCurrencyCode, fundCurrencyCode, onCreateNewFund } = currencyMismatch;
  return (
    <TextV2
      typographyVariant="label1"
      color="mono12"
    >
      <SentenceWithAction
        text={tq`Your registry switched to ${registryCurrencyCode}, but this fund collects in ${fundCurrencyCode}. <0>Create a new ${registryCurrencyCode} fund</0> to accept cards.`}
        fallbackLabel={tq`Create a new ${registryCurrencyCode} fund`}
        renderAction={
          onCreateNewFund &&
          (label => (
            <DottedTextButton
              typographyVariant="label1"
              onClick={onCreateNewFund}
            >
              {label}
            </DottedTextButton>
          ))
        }
      />
    </TextV2>
  );
};
